import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import {
  REACT_APP_WEBSOCKET_TOKEN,
  REACT_APP_WEBSOCKET_URL,
} from '../../config';
import { useTokenStore } from '../../store/useTokenStore';

type SocketIOContextType = {
  socket: Socket | null; // Expose the actual socket instance.
  sendMessage: (message: string) => void;
  lastMessage: any;
  connected: boolean;
};

const SocketIOContext = createContext<SocketIOContextType | undefined>(
  undefined
);

export const useSocketIOContext = (): SocketIOContextType => {
  const context = useContext(SocketIOContext);
  if (!context) {
    throw new Error(
      'useSocketIOContext must be used within a SocketIOProvider'
    );
  }
  return context;
};

export const SocketIOProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [connected, setConnected] = useState<boolean>(false);

  const token = useTokenStore((state) => state.accessToken);

  useEffect(() => {
    if (token) {
      const isLocalTestingMode = REACT_APP_WEBSOCKET_URL.includes('localhost');
      const wsToken = isLocalTestingMode ? REACT_APP_WEBSOCKET_TOKEN : token;

      const socketUrl = `${REACT_APP_WEBSOCKET_URL}/ws`;
      console.log('WS_TOKEN', wsToken);
      const newSocket = io(socketUrl, {
        reconnectionDelayMax: 10000,
        auth: { token: wsToken },
      });

      setSocket(newSocket);

      newSocket.on('connect', () => {
        console.log('Socket.IO connection established');
        setConnected(true);
      });

      newSocket.on('disconnect', () => {
        console.log('Socket.IO disconnected');
        setConnected(false);
      });

      newSocket.on('message', (data) => {
        setLastMessage(data);
      });

      return () => {
        newSocket.disconnect();
      };
    }
  }, [token]);

  const sendMessage = (message: string) => {
    if (socket && connected) {
      socket.emit('message', message);
    }
  };

  const value: SocketIOContextType = {
    socket,
    sendMessage,
    lastMessage,
    connected,
  };

  return (
    <SocketIOContext.Provider value={value}>
      {children}
    </SocketIOContext.Provider>
  );
};
